/**
 * Retail Story & Buyer Meeting Agent - Coming Soon
 * 
 * Clean, polished design following PIQ standards (ML Command Center style)
 * Real data from APIs - no fake data
 */

import React, { useState, useEffect } from 'react';
import { Link } from 'wouter';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import GlobalHeader from '../../components/GlobalHeader';
import ExampleQuestions from '@/components/agents/ExampleQuestions';
import { 
  Loader2, 
  Briefcase,
  Calendar,
  FileText,
  TrendingUp,
  Star,
  RefreshCw,
  Users,
  Target,
  Package,
  Lightbulb,
  Clock,
  Zap,
  Brain,
  LineChart,
  BarChart3,
  AlertTriangle,
  ChevronRight,
  CheckCircle2,
  Rocket,
  PresentationIcon,
  ClipboardList,
  MessageSquare,
  Sparkles
} from 'lucide-react';
import { useTenant } from '../../contexts/TenantContext';

const EXAMPLE_QUESTIONS = [
  "Prepare a story for my next Ulta line review",
  "What is the performance narrative for Buttah at Ulta?",
  "Give me the story for our top 5 SKUs",
  "Which SKUs are at risk for the buyer meeting?",
  "Generate a JBP executive summary"
];

const RetailStory: React.FC = () => {
  const { selectedRetailer } = useTenant();
  const [loading, setLoading] = useState(true);
  const [dataAvailable, setDataAvailable] = useState(false);

  useEffect(() => {
    checkDataStatus();
  }, [selectedRetailer?.id]);

  const checkDataStatus = async () => {
    if (!selectedRetailer?.id) {
      setLoading(false);
      return;
    }
    
    setLoading(true);
    try {
      const res = await fetch(`/api/retail-story/sku-stories?retailer_id=${selectedRetailer.id}&limit=5`).then(r => r.json());
      setDataAvailable(res.success && res.data?.length > 0);
    } catch {
      setDataAvailable(false);
    } finally {
      setLoading(false);
    }
  };

  // Connected agents
  const connectedAgents = [
    { title: 'NPD Opportunity', href: '/agents/npd-opportunity', icon: Lightbulb, gradient: 'from-amber-500 to-orange-600' },
    { title: 'Competitor Intel', href: '/agents/competitor-intel', icon: BarChart3, gradient: 'from-red-500 to-rose-600' },
    { title: 'Inventory Intelligence', href: '/agents/supply-chain', icon: Package, gradient: 'from-blue-500 to-cyan-600' },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <GlobalHeader 
        title="Retail Story & Buyer Meetings"
        subtitle="Narratives, SKU stories & meeting prep for buyer conversations"
      />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
        
        {/* Status Banner */}
        <div className="rounded-2xl shadow-lg border-2 bg-gradient-to-r from-blue-50 via-indigo-50 to-purple-50 border-blue-200 overflow-hidden">
          <div className="p-6">
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-4">
                <div className="p-3 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 shadow-lg">
                  <Briefcase className="w-8 h-8 text-white" />
                </div>
                <div>
                  <div className="flex items-center gap-3 mb-2">
                    <h2 className="text-2xl font-bold text-gray-900">Retail Story Intelligence</h2>
                    <Badge className="bg-blue-100 text-blue-800 border-blue-300 text-sm px-3 py-1">
                      Coming Soon
                    </Badge>
                  </div>
                  <p className="text-gray-600 max-w-xl">
                    AI-generated buyer meeting narratives, SKU performance stories, and JBP summaries 
                    powered by your actual sales data. Prepare for every meeting with confidence.
                  </p>
                </div>
              </div>
              <Button 
                variant="outline" 
                onClick={checkDataStatus} 
                disabled={loading}
                className="bg-white hover:bg-gray-50 shadow-sm"
              >
                {loading ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4 mr-2" />
                )}
                Check Status
              </Button>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {/* Meetings */}
          <div className="bg-gradient-to-br from-blue-50 to-blue-100/50 border border-blue-200 rounded-xl p-6 shadow-sm hover:shadow-lg transition-shadow">
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm font-medium text-blue-700">Upcoming Meetings</span>
              <div className="p-3 bg-blue-500 rounded-xl shadow-md">
                <Calendar className="h-5 w-5 text-white" />
              </div>
            </div>
            <div className="text-4xl font-bold text-blue-700 mb-2">—</div>
            <div className="flex items-center gap-1 text-sm text-blue-600">
              <Clock className="h-3 w-3" />
              <span className="font-medium">Awaiting integration</span>
            </div>
          </div>

          {/* SKU Stories */}
          <div className="bg-gradient-to-br from-emerald-50 to-emerald-100/50 border border-emerald-200 rounded-xl p-6 shadow-sm hover:shadow-lg transition-shadow">
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm font-medium text-emerald-700">SKU Stories Ready</span>
              <div className="p-3 bg-emerald-500 rounded-xl shadow-md">
                <FileText className="h-5 w-5 text-white" />
              </div>
            </div>
            <div className="text-4xl font-bold text-emerald-700 mb-2">—</div>
            <div className="flex items-center gap-1 text-sm text-emerald-600">
              <Clock className="h-3 w-3" />
              <span className="font-medium">Awaiting activation</span>
            </div>
          </div>

          {/* Star Performers */}
          <div className="bg-gradient-to-br from-amber-50 to-amber-100/50 border border-amber-200 rounded-xl p-6 shadow-sm hover:shadow-lg transition-shadow">
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm font-medium text-amber-700">Star Performers</span>
              <div className="p-3 bg-amber-500 rounded-xl shadow-md">
                <Star className="h-5 w-5 text-white" />
              </div>
            </div>
            <div className="text-4xl font-bold text-amber-700 mb-2">—</div>
            <div className="flex items-center gap-1 text-sm text-amber-600">
              <Clock className="h-3 w-3" />
              <span className="font-medium">Awaiting analysis</span>
            </div>
          </div>

          {/* At Risk */}
          <div className="bg-gradient-to-br from-orange-50 to-orange-100/50 border border-orange-200 rounded-xl p-6 shadow-sm hover:shadow-lg transition-shadow">
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm font-medium text-orange-700">Needs Attention</span>
              <div className="p-3 bg-orange-500 rounded-xl shadow-md">
                <AlertTriangle className="h-5 w-5 text-white" />
              </div>
            </div>
            <div className="text-4xl font-bold text-orange-700 mb-2">—</div>
            <div className="flex items-center gap-1 text-sm text-orange-600">
              <Clock className="h-3 w-3" />
              <span className="font-medium">Awaiting detection</span>
            </div>
          </div>
        </div>

        {/* Main Coming Soon Card */}
        <Card className="border-2 border-gray-200 shadow-lg overflow-hidden">
          <div className="bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 p-8">
            <div className="flex items-center gap-4 mb-4">
              <div className="p-3 bg-gradient-to-br from-blue-400 to-indigo-500 rounded-2xl shadow-lg">
                <Brain className="w-8 h-8 text-white" />
              </div>
              <div>
                <h3 className="text-2xl font-bold text-white">Buyer Meeting Intelligence Hub</h3>
                <p className="text-slate-300">Powered by your sales data • AI-generated narratives</p>
              </div>
            </div>
          </div>
          
          <CardContent className="p-8">
            {/* Capabilities Grid */}
            <div className="mb-8">
              <h4 className="text-lg font-semibold text-gray-900 mb-6 flex items-center gap-2">
                <Rocket className="h-5 w-5 text-blue-500" />
                What you'll get when activated
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* SKU Stories */}
                <div className="p-5 rounded-xl border-2 border-blue-100 bg-gradient-to-br from-blue-50/50 to-white hover:shadow-md transition-shadow">
                  <div className="flex items-start gap-4">
                    <div className="p-2.5 rounded-xl bg-blue-100">
                      <FileText className="h-6 w-6 text-blue-600" />
                    </div>
                    <div className="flex-1">
                      <h5 className="font-semibold text-gray-900 mb-1">SKU Performance Stories</h5>
                      <p className="text-sm text-gray-600">Auto-generated narratives for every SKU with trends and actions</p>
                    </div>
                    <CheckCircle2 className="h-5 w-5 text-emerald-500 flex-shrink-0" />
                  </div>
                </div>

                {/* Meeting Prep */}
                <div className="p-5 rounded-xl border-2 border-emerald-100 bg-gradient-to-br from-emerald-50/50 to-white hover:shadow-md transition-shadow">
                  <div className="flex items-start gap-4">
                    <div className="p-2.5 rounded-xl bg-emerald-100">
                      <PresentationIcon className="h-6 w-6 text-emerald-600" />
                    </div>
                    <div className="flex-1">
                      <h5 className="font-semibold text-gray-900 mb-1">Meeting Prep Decks</h5>
                      <p className="text-sm text-gray-600">QBR, line review, and JBP materials generated automatically</p>
                    </div>
                    <CheckCircle2 className="h-5 w-5 text-emerald-500 flex-shrink-0" />
                  </div>
                </div>

                {/* Performance Tiers */}
                <div className="p-5 rounded-xl border-2 border-amber-100 bg-gradient-to-br from-amber-50/50 to-white hover:shadow-md transition-shadow">
                  <div className="flex items-start gap-4">
                    <div className="p-2.5 rounded-xl bg-amber-100">
                      <TrendingUp className="h-6 w-6 text-amber-600" />
                    </div>
                    <div className="flex-1">
                      <h5 className="font-semibold text-gray-900 mb-1">Performance Tiering</h5>
                      <p className="text-sm text-gray-600">Stars, growers, at-risk SKUs identified with talking points</p>
                    </div>
                    <CheckCircle2 className="h-5 w-5 text-emerald-500 flex-shrink-0" />
                  </div>
                </div>

                {/* Talking Points */}
                <div className="p-5 rounded-xl border-2 border-purple-100 bg-gradient-to-br from-purple-50/50 to-white hover:shadow-md transition-shadow">
                  <div className="flex items-start gap-4">
                    <div className="p-2.5 rounded-xl bg-purple-100">
                      <MessageSquare className="h-6 w-6 text-purple-600" />
                    </div>
                    <div className="flex-1">
                      <h5 className="font-semibold text-gray-900 mb-1">Smart Talking Points</h5>
                      <p className="text-sm text-gray-600">AI-crafted narratives highlighting wins and addressing challenges</p>
                    </div>
                    <CheckCircle2 className="h-5 w-5 text-emerald-500 flex-shrink-0" />
                  </div>
                </div>
              </div>
            </div>

            {/* How It Works */}
            <div className="bg-gradient-to-br from-gray-50 to-gray-100/50 rounded-2xl p-6 border border-gray-200">
              <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Zap className="h-5 w-5 text-blue-500" />
                How Retail Story Works
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-blue-500 text-white font-bold flex items-center justify-center flex-shrink-0 shadow-md">
                    1
                  </div>
                  <div>
                    <h5 className="font-semibold text-gray-900 mb-1">Analyze Performance</h5>
                    <p className="text-sm text-gray-600">AI scans your SKU data to identify trends, wins, and risks</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-blue-500 text-white font-bold flex items-center justify-center flex-shrink-0 shadow-md">
                    2
                  </div>
                  <div>
                    <h5 className="font-semibold text-gray-900 mb-1">Generate Narratives</h5>
                    <p className="text-sm text-gray-600">Creates compelling stories with data-backed talking points</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-blue-500 text-white font-bold flex items-center justify-center flex-shrink-0 shadow-md">
                    3
                  </div>
                  <div>
                    <h5 className="font-semibold text-gray-900 mb-1">Prep for Meetings</h5>
                    <p className="text-sm text-gray-600">Walk into every buyer meeting with confidence and clarity</p>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Example Questions */}
        <ExampleQuestions 
          questions={EXAMPLE_QUESTIONS}
          accentColor="blue"
          title="Ask the Retail Story Agent"
          description="Click any question to prepare buyer meeting content"
        />

        {/* Connected Agents */}
        <div>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Connected Agents</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {connectedAgents.map((agent) => {
              const Icon = agent.icon;
              return (
                <Link key={agent.href} href={agent.href}>
                  <div className="group bg-white border border-gray-200 rounded-xl p-5 shadow-sm hover:shadow-lg hover:border-blue-300 transition-all cursor-pointer">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`p-2.5 rounded-xl bg-gradient-to-br ${agent.gradient} shadow-md`}>
                          <Icon className="h-5 w-5 text-white" />
                        </div>
                        <span className="font-semibold text-gray-900">{agent.title}</span>
                      </div>
                      <ChevronRight className="h-5 w-5 text-gray-400 group-hover:text-blue-500 transition-colors" />
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>

        {/* Roadmap Preview */}
        <div>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Retail Story Roadmap</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 rounded-lg bg-gradient-to-br from-blue-500 to-cyan-600 shadow-sm">
                  <FileText className="h-5 w-5 text-white" />
                </div>
                <h3 className="font-semibold text-gray-900">SKU Narratives</h3>
              </div>
              <p className="text-sm text-gray-600 mb-3">Auto-generated performance stories</p>
              <Badge variant="outline" className="text-xs text-gray-500">Coming Soon</Badge>
            </div>

            <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600 shadow-sm">
                  <ClipboardList className="h-5 w-5 text-white" />
                </div>
                <h3 className="font-semibold text-gray-900">JBP Generator</h3>
              </div>
              <p className="text-sm text-gray-600 mb-3">Executive summaries on demand</p>
              <Badge variant="outline" className="text-xs text-gray-500">Coming Soon</Badge>
            </div>

            <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 rounded-lg bg-gradient-to-br from-amber-500 to-orange-600 shadow-sm">
                  <Target className="h-5 w-5 text-white" />
                </div>
                <h3 className="font-semibold text-gray-900">Meeting Scheduler</h3>
              </div>
              <p className="text-sm text-gray-600 mb-3">Calendar integration & prep</p>
              <Badge variant="outline" className="text-xs text-gray-500">Coming Soon</Badge>
            </div>

            <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 rounded-lg bg-gradient-to-br from-purple-500 to-pink-600 shadow-sm">
                  <Sparkles className="h-5 w-5 text-white" />
                </div>
                <h3 className="font-semibold text-gray-900">Presentation Export</h3>
              </div>
              <p className="text-sm text-gray-600 mb-3">One-click slide generation</p>
              <Badge variant="outline" className="text-xs text-gray-500">Coming Soon</Badge>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default RetailStory;
